/**
 * `contacts` procedure group.
 *
 * A **person** is a real human; a **contact** is one tenant's record about a person.
 * Only `firstName` is mandatory — that is the spec, and it is what the founder
 * actually has 20 seconds after meeting someone.
 */

import { z } from 'zod';
import {
  cursorSchema,
  isoDateTimeSchema,
  nonBlankString,
  paginationInputSchema,
  uuidSchema,
} from './common.ts';
import {
  accessSourceFilterSchema,
  accessSourceSchema,
  contactSortSchema,
  contactTypeSchema,
  emailLabelSchema,
  emailVerificationStatusSchema,
  enrichmentStatusSchema,
  fieldSourceSchema,
  interactionKindSchema,
  phoneLabelSchema,
  shareScopeSchema,
  visibilitySchema,
} from './enums.ts';

/**
 * Permissive enough for what a user actually pastes (country subdomains, tracking
 * query strings, trailing slash); `@crm/domain.normalizeLinkedin` canonicalises to
 * `https://www.linkedin.com/in/<slug>` server-side. Rejects non-LinkedIn URLs and
 * LinkedIn URLs that are not /in/ profiles (company pages, feed posts).
 */
export const LINKEDIN_PROFILE_URL_PATTERN =
  /^https?:\/\/(?:[a-z0-9-]{1,20}\.)?linkedin\.com\/in\/[^/?#\s]{1,150}\/?(?:[?#].*)?$/i;

export const linkedinProfileUrlSchema = z
  .string()
  .min(1)
  .max(500)
  .regex(LINKEDIN_PROFILE_URL_PATTERN, 'expected a linkedin.com/in/ profile URL');

/**
 * What people actually have on hand for an X account: `@handle`, a bare
 * handle, or a profile URL on x.com/twitter.com. `@crm/domain.normalizeX`
 * canonicalizes to the lowercase handle server-side; a value that cannot be is
 * refused there with a legible message rather than partially matched here.
 */
export const xAccountInputSchema = z.string().min(1).max(500);

export const contactEmailAddressSchema = z.email().max(320);

/** Raw as typed; `@crm/domain.normalizePhone` derives `e164` / `last9` server-side. */
export const rawPhoneSchema = z.string().min(3).max(64);

// --- entities ----------------------------------------------------------------

export const contactEmailSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  email: z.string(),
  emailNormalized: z.string(),
  label: emailLabelSchema,
  isPrimary: z.boolean(),
  verificationStatus: emailVerificationStatusSchema,
  source: fieldSourceSchema,
  confidence: z.number().min(0).max(1).nullable(),
  lastSeenAt: isoDateTimeSchema.nullable(),
  bouncedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

export const contactPhoneSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  raw: z.string(),
  e164: z.string().nullable(),
  last9: z.string().nullable(),
  label: phoneLabelSchema,
  isPrimary: z.boolean(),
  isMobile: z.boolean().nullable(),
  source: fieldSourceSchema,
  createdAt: isoDateTimeSchema,
});

/**
 * How the caller reaches this contact. `scope` is `null` for owned rows (an owner is
 * not scope-limited); `canEdit` is false for every shared row — a row is written only
 * by its owning org, grants confer READ only.
 */
export const contactAccessSchema = z.object({
  source: accessSourceSchema,
  scope: shareScopeSchema.nullable(),
  canEdit: z.boolean(),
  canAdopt: z.boolean(),
  ownerOrgId: uuidSchema,
  ownerOrgName: z.string().nullable(),
  shareId: uuidSchema.nullable(),
});

/**
 * The full contact row. `linkedinUrl` is a plain string here (not the strict input
 * pattern) so historical rows can never fail response validation.
 *
 * ## Why the owner-bookkeeping fields are nullable
 *
 * A contact reaching the caller through a cross-tenant grant is serialized by
 * `projectForScope` (design §2.2 layer 2), a static field WHITELIST. Everything
 * outside that whitelist is *structurally absent* from a shared row — including
 * `visibility`, `enrichmentStatus`, `interactionCount`, `createdAt` and
 * `updatedAt`, which are listed in `NEVER_SHARED` or simply omitted.
 *
 * So those five are `.nullable()`, and **null means "not visible at this scope"**,
 * never "zero" or "unknown to the server". They are always populated for an owned
 * row (`access.source === 'owned'`). Fabricating values for them instead — a
 * synthetic `updatedAt`, say — would put a false timestamp in the API that a sync
 * client would happily adopt as a watermark.
 */
export const contactSchema = z.object({
  id: uuidSchema,
  /** The OWNING org. For a shared row this is the grantor, not the caller's org. */
  orgId: uuidSchema,
  personId: uuidSchema.nullable(),
  /**
   * Empty string only in one case: a shared row whose name came from a vendor and
   * whose owner has not enabled `allowEnrichedFieldSharing`, so the projection
   * withheld it. Never empty on an owned row.
   */
  firstName: z.string(),
  lastName: z.string().nullable(),
  contactType: contactTypeSchema.nullable(),
  companyId: uuidSchema.nullable(),
  companyName: z.string().nullable(),
  title: z.string().nullable(),
  notes: z.string().nullable(),
  linkedinUrl: z.string().nullable(),
  linkedinSlug: z.string().nullable(),
  /** Canonical lowercase handle, no `@`. Render as `@handle` -> https://x.com/<handle>. */
  xHandle: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhoneE164: z.string().nullable(),
  metAtEventId: uuidSchema.nullable(),
  /** Resolved name of the met-at event; the only met-at field a share exposes. */
  metAtEventName: z.string().nullable(),
  metAt: isoDateTimeSchema.nullable(),
  ownerUserId: uuidSchema.nullable(),
  visibility: visibilitySchema.nullable(),
  enrichmentStatus: enrichmentStatusSchema.nullable(),
  enrichedAt: isoDateTimeSchema.nullable(),
  lastInteractionAt: isoDateTimeSchema.nullable(),
  interactionCount: z.int().min(0).nullable(),
  adoptedFromShareId: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema.nullable(),
  updatedAt: isoDateTimeSchema.nullable(),
  deletedAt: isoDateTimeSchema.nullable(),
});

/**
 * The list-row projection: everything `ContactTable` renders, nothing more.
 *
 * The same nullability rule as `contactSchema` applies — see its doc comment.
 * `listIds` is always `[]` for a shared row: list membership is an organisational
 * fact about the OWNER's CRM and is in no share scope.
 */
export const contactSummarySchema = z.object({
  id: uuidSchema,
  firstName: z.string(),
  lastName: z.string().nullable(),
  contactType: contactTypeSchema.nullable(),
  companyId: uuidSchema.nullable(),
  companyName: z.string().nullable(),
  title: z.string().nullable(),
  linkedinUrl: z.string().nullable(),
  xHandle: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhoneE164: z.string().nullable(),
  metAtEventId: uuidSchema.nullable(),
  metAtEventName: z.string().nullable(),
  visibility: visibilitySchema.nullable(),
  enrichmentStatus: enrichmentStatusSchema.nullable(),
  lastInteractionAt: isoDateTimeSchema.nullable(),
  interactionCount: z.int().min(0).nullable(),
  listIds: z.array(uuidSchema),
  /**
   * What the most recent interaction WAS, so the list can render the design's
   * source icon next to the relative time instead of a bare "3d ago".
   *
   * `kind`, not `channel`, and that is a deliberate substitution. The rollup
   * trigger maintaining `last_interaction_at` tracked neither, so one of them
   * had to become a maintained column (migration 0006). `interactions.kind` is
   * NOT NULL while `interactions.channel` is nullable — a note carries
   * `kind='note'` and `channel='manual'`, a met-at row carries
   * `kind='met_at_event'` and no channel at all — so keying the icon off `kind`
   * renders something truthful for every row instead of an "unknown" badge on
   * the rows the user creates most often.
   *
   * Null means no interactions yet (or a shared row, where interaction
   * bookkeeping is not in any scope — see `contactSchema`'s doc comment).
   */
  lastInteractionKind: interactionKindSchema.nullable(),
  updatedAt: isoDateTimeSchema.nullable(),
  access: contactAccessSchema,
});

/** The smallest useful identity, for embedding in feeds and share listings. */
export const contactRefSchema = z.object({
  id: uuidSchema,
  firstName: z.string(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
});

/**
 * One `contacts.field_sources` entry: where a scalar field's current value came
 * from, how confident the writer was, and when it landed.
 *
 * The keys are the storage spelling (`title`, `company_name_raw`, …), passed
 * through verbatim rather than camelCased — the merge layer writes them and
 * `contact_field_candidates.field_path` reads them, and renaming in transit
 * would make the wire disagree with the candidate rows that reference it.
 */
export const fieldSourceEntrySchema = z.object({
  src: z.string().nullish(),
  conf: z.number().min(0).max(1).nullish(),
  at: z.string().nullish(),
});

export const fieldSourcesSchema = z.record(z.string(), fieldSourceEntrySchema);

export const contactDetailSchema = contactSchema.extend({
  emails: z.array(contactEmailSchema),
  phones: z.array(contactPhoneSchema),
  /**
   * Per-field provenance — **owned rows only, and null for every shared row.**
   *
   * `fieldSources` is named in `@crm/db`'s `NEVER_SHARED` list, so the scope
   * projection strips it structurally for a shared read; this field being
   * nullable is the wire's half of that same fact rather than a second rule that
   * could drift from it. Two reasons it must never cross a grant: it is the
   * ledger of which values were PURCHASED (the legal reason enriched fields do
   * not cross a share by default), and it is per-vendor telemetry about the
   * owner's enrichment spend, which is nobody else's business.
   *
   * The web `ProvenanceField` primitive reads it; an absent key means the value
   * was typed by a human (every enrichment write stamps provenance), which is
   * the same fail-closed rule `projectForScope` applies.
   */
  fieldSources: fieldSourcesSchema.nullable(),
  access: contactAccessSchema,
});

// --- matchKeys ---------------------------------------------------------------

/**
 * The pushed-down key bundle for ONE contact (design-clients §3.2(c)).
 *
 * Server-keys-DOWN is the whole privacy posture of the iPhone lane: the address
 * book never leaves the device, so the device needs the CRM's keys to match
 * against locally. Every array is deduplicated and sorted — that is not tidiness,
 * it is what makes the bundle's serialization deterministic and therefore what
 * makes `version` a usable ETag.
 */
export const contactMatchKeyBundleSchema = z.object({
  contactId: uuidSchema,
  /** `@crm/domain.normalizeName().key`, or null when there is no usable name. */
  nameKey: z.string().nullable(),
  /** `@crm/domain.normalizeCompany()`, or null. */
  org: z.string().nullable(),
  /** EVERY address on the contact, normalized. Not just the primary. */
  emails: z.array(z.string()),
  /** Corroborating domains, FREEMAIL EXCLUDED — see the mobile matcher. */
  emailDomains: z.array(z.string()),
  /** Every strict-E.164 number. Tier-A material. */
  phonesE164: z.array(z.string()),
  /** Every last-9 fallback key. Tier-B material. */
  phonesLast9: z.array(z.string()),
});

/**
 * `version` is the conditional-GET half.
 *
 * oRPC has no `If-None-Match` (its transport is a JSON envelope, not a REST
 * cache), so the 304 is modelled IN BAND: send back the version you hold and the
 * server answers `{version, changed: false}` with no bundles. The alternative —
 * shipping the whole key set on every foreground — is a few hundred KB per app
 * open on a phone, for a payload that changes when a contact is edited.
 */
export const contactMatchKeysInputSchema = z.object({
  version: z.string().min(1).max(128).optional(),
});

export const contactMatchKeysOutputSchema = z.object({
  /** sha256 over the sorted, serialized bundles. Stable across page order. */
  version: z.string(),
  /** False when the caller's `version` matched: `bundles` is then empty. */
  changed: z.boolean(),
  bundles: z.array(contactMatchKeyBundleSchema),
  /** Bundle count in the CURRENT version, even when `changed` is false. */
  contactCount: z.int().min(0),
  generatedAt: isoDateTimeSchema,
});

// --- inputs ------------------------------------------------------------------

export const contactEmailInputSchema = z.object({
  email: contactEmailAddressSchema,
  label: emailLabelSchema.default('work'),
  isPrimary: z.boolean().default(false),
});

export const contactPhoneInputSchema = z.object({
  raw: rawPhoneSchema,
  label: phoneLabelSchema.default('mobile'),
  isPrimary: z.boolean().default(false),
});

/**
 * Filters are all URL-search-param shaped so web views stay linkable.
 * Cursor pagination is on `(sort, id)`; `sort` selects the cursor encoding.
 */
export const contactListInputSchema = paginationInputSchema.extend({
  q: z.string().min(1).max(200).optional(),
  type: z.array(contactTypeSchema).max(4).optional(),
  listId: z.array(uuidSchema).max(20).optional(),
  companyId: uuidSchema.optional(),
  metAtEventId: uuidSchema.optional(),
  tag: z.array(z.string().min(1).max(64)).max(20).optional(),
  hasEmail: z.boolean().optional(),
  hasPhone: z.boolean().optional(),
  hasLinkedin: z.boolean().optional(),
  hasFollowUp: z.boolean().optional(),
  enrichmentStatus: z.array(enrichmentStatusSchema).max(6).optional(),
  lastInteractionBefore: isoDateTimeSchema.optional(),
  source: accessSourceFilterSchema.default('all'),
  includeDeleted: z.boolean().default(false),
  sort: contactSortSchema.default('updated_at'),
});

export const contactListOutputSchema = z.object({
  items: z.array(contactSummarySchema),
  nextCursor: cursorSchema.nullable(),
  /** Always present; `null` when the backend could not count cheaply. */
  totalEstimate: z.int().min(0).nullable(),
});

export const contactGetInputSchema = z.object({
  id: uuidSchema,
});

export const contactCreateInputSchema = z.object({
  firstName: nonBlankString(200, 'firstName'),
  lastName: z.string().max(200).nullish(),
  contactType: contactTypeSchema.nullish(),
  companyId: uuidSchema.nullish(),
  /** Kept verbatim as `company_name_raw`; resolution to a company row is async. */
  companyName: z.string().max(300).nullish(),
  title: z.string().max(300).nullish(),
  notes: z.string().max(20_000).nullish(),
  linkedinUrl: linkedinProfileUrlSchema.nullish(),
  xHandle: xAccountInputSchema.nullish(),
  emails: z.array(contactEmailInputSchema).max(20).default([]),
  phones: z.array(contactPhoneInputSchema).max(20).default([]),
  metAtEventId: uuidSchema.nullish(),
  metAt: isoDateTimeSchema.nullish(),
  visibility: visibilitySchema.default('org'),
  ownerUserId: uuidSchema.nullish(),
  /** Set when the record originates from an accepted duplicate/candidate suggestion. */
  personId: uuidSchema.nullish(),
});

/**
 * Every key is optional; absent means untouched, `null` means clear.
 * Emails and phones are managed through their own procedures, not here.
 */
export const contactUpdateInputSchema = z.object({
  id: uuidSchema,
  firstName: nonBlankString(200, 'firstName').optional(),
  lastName: z.string().max(200).nullish(),
  contactType: contactTypeSchema.nullish(),
  companyId: uuidSchema.nullish(),
  companyName: z.string().max(300).nullish(),
  title: z.string().max(300).nullish(),
  notes: z.string().max(20_000).nullish(),
  linkedinUrl: linkedinProfileUrlSchema.nullish(),
  /** Editable (unlike `linkedinUrl`): a handle is renameable, not identity-grade. */
  xHandle: xAccountInputSchema.nullish(),
  metAtEventId: uuidSchema.nullish(),
  metAt: isoDateTimeSchema.nullish(),
  visibility: visibilitySchema.optional(),
  ownerUserId: uuidSchema.nullish(),
});

/** Soft delete; the server also writes a `contact_suppressions` row. */
export const contactDeleteInputSchema = z.object({
  id: uuidSchema,
});

export const contactDeleteOutputSchema = z.object({
  ok: z.literal(true),
  id: uuidSchema,
  deletedAt: isoDateTimeSchema,
});

export const contactRestoreInputSchema = z.object({
  id: uuidSchema,
});

export const contactEmailAddInputSchema = z.object({
  contactId: uuidSchema,
  email: contactEmailAddressSchema,
  label: emailLabelSchema.default('work'),
  isPrimary: z.boolean().default(false),
});

export const contactEmailRemoveInputSchema = z.object({
  contactId: uuidSchema,
  emailId: uuidSchema,
});

export const contactPhoneAddInputSchema = z.object({
  contactId: uuidSchema,
  raw: rawPhoneSchema,
  label: phoneLabelSchema.default('mobile'),
  isPrimary: z.boolean().default(false),
});

export const contactPhoneRemoveInputSchema = z.object({
  contactId: uuidSchema,
  phoneId: uuidSchema,
});

// --- inferred types ----------------------------------------------------------

export type Contact = z.output<typeof contactSchema>;
export type ContactDetail = z.output<typeof contactDetailSchema>;
export type ContactSummary = z.output<typeof contactSummarySchema>;
export type ContactRef = z.output<typeof contactRefSchema>;
export type ContactEmail = z.output<typeof contactEmailSchema>;
export type ContactPhone = z.output<typeof contactPhoneSchema>;
export type ContactAccess = z.output<typeof contactAccessSchema>;
export type ContactFieldSources = z.output<typeof fieldSourcesSchema>;
export type ContactFieldSourceEntry = z.output<typeof fieldSourceEntrySchema>;
export type ContactMatchKeyBundle = z.output<typeof contactMatchKeyBundleSchema>;
export type ContactMatchKeysOutput = z.output<typeof contactMatchKeysOutputSchema>;
export type ContactCreateInput = z.input<typeof contactCreateInputSchema>;
export type ContactUpdateInput = z.input<typeof contactUpdateInputSchema>;
export type ContactListInput = z.input<typeof contactListInputSchema>;
